/**
 * TownMenuPanel — Rich town interaction panel for Spellroads.
 *
 * Opens when the party enters or interacts with a town.
 * Exposes sections: Blacksmith, General Store, Inn, Tavern, Temple,
 * Party Management, and Leave Town.
 *
 * Usage:
 *   TownMenuPanel.open({ name: 'Ironhold', type: 'trading_post',
 *     services: ['blacksmith', 'general_store', 'inn'] });
 *
 * The panel is also available via:
 *   window.__openTownMenu(townData)
 *   EventBus.emit('town:enter', townData)
 *
 * @module TownMenuPanel
 */

import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';
import WorldState from '../world/WorldState.js';
import DialoguePanel from './DialoguePanel.js';
import CraftingSystem from '../systems/CraftingSystem.js';
import CompanionSystem from '../systems/CompanionSystem.js';

// ─── Service definitions ──────────────────────────────────────────────────────

/**
 * Static metadata for every possible town service.
 * `id` matches the string used in townData.services[].
 */
const SERVICE_META = {
  blacksmith: {
    id: 'blacksmith',
    label: 'Blacksmith',
    icon: '\u2694\uFE0F',
    description: 'Buy and sell weapons, armor, and equipment.',
  },
  general_store: {
    id: 'general_store',
    label: 'General Store',
    icon: '\uD83C\uDFEA',
    description: 'Consumables, tools, camping supplies, and everyday goods.',
  },
  inn: {
    id: 'inn',
    label: 'Inn',
    icon: '\uD83C\uDFE8',
    description: 'Rest for the night and restore HP and status.',
  },
  tavern: {
    id: 'tavern',
    label: 'Tavern',
    icon: '\uD83C\uDF7A',
    description: 'Hire mercenaries and hear local rumors.',
  },
  temple: {
    id: 'temple',
    label: 'Temple',
    icon: '\u26EA',
    description: 'Healing services, cure status effects, and blessings.',
  },
  party: {
    id: 'party',
    label: 'Party',
    icon: '\uD83D\uDC65',
    description: 'Rearrange formation, check stats, and manage equipment.',
  },
  notices_board: {
    id: 'notices_board',
    label: 'Board',
    icon: '\uD83D\uDCCB',
    description: 'Local job notices, bounties, and requests from townsfolk.',
  },
};

/** Order services appear in the sidebar. */
const SERVICE_ORDER = [
  'blacksmith',
  'general_store',
  'inn',
  'tavern',
  'temple',
  'party',
  'notices_board',
];

// ─── Flavor text pools ────────────────────────────────────────────────────────

const TAVERN_RUMORS = [
  'A prospector swears he found dragon scales on the eastern pass — then went quiet.',
  'The old mill on the hill has been lit at night. No one lives there.',
  'Caravan raiders near Stonegate Ford. Three wagons gone, no bodies found.',
  'A wandering seer predicted rain for forty days beginning at the next new moon.',
  'Someone is buying up all the salt in the region. No one knows why.',
  'The river changed course overnight near Ashvale. The village elder is spooked.',
  'A knight in black armor was seen on the ridge. He was just watching.',
  'The barrow mounds east of town have been disturbed. Locals avoid the area.',
  'Word is a sea captain is paying three times the going rate for able fighters.',
  'Strange lights over the tundra last week — blue-white, moving fast and silent.',
];

const INN_NAMES = [
  'The Hearthstone Rest',
  'The Sleeping Boar',
  'The Wanderer\'s Lantern',
  'The Frost & Flagon',
  'The Old Anchor Inn',
];

// ─── Inline styles ────────────────────────────────────────────────────────────

const STYLE_ID = 'spellroads-town-menu-style';

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
/* ══════════════════════════════════════════════════════════════════════════
   TownMenuPanel — Spellroads
   ══════════════════════════════════════════════════════════════════════════ */

#town-menu-panel {
  position: fixed;
  inset: 0;
  z-index: 250;
  display: none;
  flex-direction: column;
  background: rgba(6, 4, 2, 0.97);
  pointer-events: all;
  overflow: hidden;
  font-family: 'Georgia', 'Times New Roman', serif;
  color: #e8d5b0;
}

#town-menu-panel.tmp-visible { display: flex; }

/* ── Header ────────────────────────────────────────────────────────────── */
.tmp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: clamp(8px, 1.5vh, 14px) clamp(12px, 2vw, 24px);
  background: rgba(0,0,0,0.55);
  border-bottom: 1px solid rgba(200,160,32,0.25);
  flex-shrink: 0;
  gap: 12px;
}

.tmp-header-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.tmp-town-name {
  font-size: clamp(1rem, 2vw, 1.45rem);
  font-weight: 700;
  color: #c8a020;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tmp-town-type {
  font-size: clamp(0.6rem, 1vw, 0.78rem);
  color: #907860;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.tmp-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.tmp-gold-display {
  font-size: clamp(0.8rem, 1.2vw, 0.95rem);
  color: #d4aa40;
  font-weight: 600;
  white-space: nowrap;
}

.tmp-gold-icon { color: #c8a020; margin-right: 4px; }

.tmp-close-btn {
  background: none;
  border: 1px solid rgba(200,160,32,0.3);
  color: #a09070;
  cursor: pointer;
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 4px;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s, border-color 0.15s;
}

.tmp-close-btn:hover,
.tmp-close-btn:focus-visible {
  color: #c8a020;
  border-color: #c8a020;
  outline: 2px solid #e8c040;
  outline-offset: 2px;
}

/* ── Body: sidebar + content ────────────────────────────────────────────── */
.tmp-body {
  flex: 1;
  display: grid;
  grid-template-columns: 210px 1fr;
  overflow: hidden;
  min-height: 0;
}

/* ── Sidebar ────────────────────────────────────────────────────────────── */
.tmp-sidebar {
  display: flex;
  flex-direction: column;
  background: rgba(0,0,0,0.35);
  border-right: 1px solid rgba(200,160,32,0.15);
  overflow-y: auto;
  padding: 12px 8px;
  gap: 4px;
  flex-shrink: 0;
}

.tmp-sidebar-label {
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #605040;
  padding: 4px 8px 8px;
}

.tmp-service-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 44px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  color: #a09070;
  font-family: inherit;
  font-size: clamp(0.7rem, 1.1vw, 0.82rem);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}

.tmp-service-btn:hover,
.tmp-service-btn:focus-visible {
  background: rgba(200,160,32,0.08);
  color: #c8d0a0;
  border-color: rgba(200,160,32,0.2);
  outline: 2px solid #e8c040;
  outline-offset: 2px;
}

.tmp-service-btn.active {
  background: rgba(200,160,32,0.14);
  color: #c8a020;
  border-color: rgba(200,160,32,0.4);
}

.tmp-service-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.tmp-service-icon {
  font-size: 1.15rem;
  flex-shrink: 0;
  width: 24px;
  text-align: center;
}

.tmp-leave-btn {
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid rgba(200,160,32,0.12);
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* ── Content area ───────────────────────────────────────────────────────── */
.tmp-content {
  overflow-y: auto;
  padding: clamp(16px, 2vw, 28px);
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.tmp-section-title {
  font-size: clamp(0.9rem, 1.6vw, 1.15rem);
  font-weight: 700;
  color: #c8a020;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid rgba(200,160,32,0.2);
  padding-bottom: 8px;
}

.tmp-section-desc {
  font-size: clamp(0.72rem, 1.1vw, 0.85rem);
  color: #907860;
  line-height: 1.5;
}

/* ── Item list ──────────────────────────────────────────────────────────── */
.tmp-item-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tmp-item-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 4px;
  transition: background 0.1s, border-color 0.1s;
}

.tmp-item-row:hover { background: rgba(200,160,32,0.06); border-color: rgba(200,160,32,0.18); }

.tmp-item-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }

.tmp-item-name {
  font-size: clamp(0.78rem, 1.2vw, 0.9rem);
  color: #d0c0a0;
  font-weight: 600;
}

.tmp-item-desc {
  font-size: clamp(0.65rem, 0.95vw, 0.75rem);
  color: #706050;
  line-height: 1.4;
}

.tmp-item-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.tmp-item-price {
  font-size: clamp(0.72rem, 1.05vw, 0.82rem);
  color: #d4aa40;
  font-weight: 600;
  white-space: nowrap;
}

.tmp-action-btn {
  min-height: 36px;
  min-width: 64px;
  padding: 0 14px;
  background: rgba(200,160,32,0.1);
  border: 1px solid rgba(200,160,32,0.35);
  border-radius: 3px;
  color: #c8a020;
  font-family: inherit;
  font-size: clamp(0.65rem, 0.95vw, 0.75rem);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}

.tmp-action-btn:hover,
.tmp-action-btn:focus-visible {
  background: rgba(200,160,32,0.22);
  color: #f0d060;
  outline: 2px solid #e8c040;
  outline-offset: 2px;
}

.tmp-action-btn:disabled {
  opacity: 0.38;
  cursor: default;
}

/* ── Rumor / flavor text ────────────────────────────────────────────────── */
.tmp-rumor-box {
  background: rgba(60,40,10,0.4);
  border: 1px solid rgba(160,120,30,0.25);
  border-radius: 4px;
  padding: 14px 18px;
  font-size: clamp(0.78rem, 1.15vw, 0.88rem);
  color: #c0a870;
  font-style: italic;
  line-height: 1.6;
}

.tmp-rumor-source {
  display: block;
  margin-top: 6px;
  font-size: 0.7rem;
  color: #705840;
  font-style: normal;
}

/* ── Confirm dialog ─────────────────────────────────────────────────────── */
.tmp-confirm-box {
  background: rgba(20,14,6,0.95);
  border: 1px solid rgba(200,160,32,0.35);
  border-radius: 6px;
  padding: 18px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.tmp-confirm-title {
  font-size: clamp(0.88rem, 1.35vw, 1rem);
  color: #c8a020;
  font-weight: 700;
}

.tmp-confirm-body {
  font-size: clamp(0.75rem, 1.1vw, 0.85rem);
  color: #a09070;
  line-height: 1.5;
}

.tmp-confirm-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

/* ── Mercenary list ─────────────────────────────────────────────────────── */
.tmp-merc-row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 4px;
}

.tmp-merc-row:hover { background: rgba(200,160,32,0.06); border-color: rgba(200,160,32,0.18); }

.tmp-merc-name {
  font-size: clamp(0.78rem, 1.2vw, 0.9rem);
  color: #d0c0a0;
  font-weight: 600;
}

.tmp-merc-class {
  font-size: clamp(0.65rem, 0.9vw, 0.72rem);
  color: #706050;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.tmp-merc-wage {
  font-size: clamp(0.72rem, 1vw, 0.82rem);
  color: #d4aa40;
  white-space: nowrap;
}

/* ── Stat row ───────────────────────────────────────────────────────────── */
.tmp-stat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.tmp-stat-cell {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 4px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}

.tmp-stat-label {
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #605040;
}

.tmp-stat-value {
  font-size: clamp(0.9rem, 1.4vw, 1.1rem);
  font-weight: 700;
  color: #d0b870;
}

/* ── Responsive ─────────────────────────────────────────────────────────── */
@media (max-width: 767px) {
  .tmp-body {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }

  .tmp-sidebar {
    flex-direction: row;
    flex-wrap: wrap;
    overflow-x: auto;
    overflow-y: hidden;
    border-right: none;
    border-bottom: 1px solid rgba(200,160,32,0.15);
    padding: 8px;
    gap: 6px;
  }

  .tmp-leave-btn {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
    border-left: 1px solid rgba(200,160,32,0.12);
    padding-left: 8px;
    flex-direction: row;
    align-items: center;
  }

  .tmp-sidebar-label { display: none; }
}
`;
  document.head.appendChild(s);
}

// ─── TownMenuPanel ────────────────────────────────────────────────────────────

const TownMenuPanel = {
  /** @type {HTMLElement|null} */
  _el: null,

  /** @type {string} Currently active service tab */
  _activeService: null,

  /** @type {object|null} Current town data */
  _townData: null,

  /** @type {boolean} */
  _visible: false,

  // ─── Public API ─────────────────────────────────────────────────────────

  /**
   * Open the town menu for a specific town.
   * @param {object} townData
   * @param {string} townData.name — Display name
   * @param {string} [townData.type] — Town type label (e.g. 'trading_post')
   * @param {string[]} [townData.services] — Available service IDs
   */
  open(townData = {}) {
    injectStyles();

    this._townData = {
      name:     townData.name     ?? 'Town',
      type:     townData.type     ?? 'settlement',
      services: townData.services ?? Object.keys(SERVICE_META),
      level:    townData.level    ?? 1,
    };

    if (!this._el) this._buildDOM();

    this._renderHeader();
    this._renderSidebar();

    // Select first available service
    const firstService = SERVICE_ORDER.find(s => this._townData.services.includes(s))
      ?? this._townData.services[0];
    this._selectService(firstService);

    this._el.classList.add('tmp-visible');
    this._visible = true;

    DebugLog.info(`[debug] TownMenuPanel opened: ${this._townData.name}`);
    EventBus.emit('town:opened', { name: this._townData.name });

    // Focus first interactive element
    requestAnimationFrame(() => {
      this._el?.querySelector('.tmp-service-btn:not(:disabled)')?.focus();
    });
  },

  /**
   * Close the town menu.
   */
  close() {
    if (!this._visible) return;
    this._el?.classList.remove('tmp-visible');
    this._visible = false;
    this._townData = null;
    this._activeService = null;

    DebugLog.info('[debug] TownMenuPanel closed');
    EventBus.emit('town:closed', {});
  },

  /** @returns {boolean} */
  isOpen() { return this._visible; },

  // ─── DOM construction ────────────────────────────────────────────────────

  _buildDOM() {
    document.getElementById('town-menu-panel')?.remove();

    const el = document.createElement('div');
    el.id = 'town-menu-panel';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Town Menu');

    el.innerHTML = `
      <div class="tmp-header" id="tmp-header"></div>
      <div class="tmp-body">
        <nav class="tmp-sidebar" id="tmp-sidebar" aria-label="Town services"></nav>
        <div class="tmp-content" id="tmp-content" role="region" aria-live="polite"></div>
      </div>
    `;

    document.body.appendChild(el);
    this._el = el;

    // Close on Escape
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); this.close(); }
    });
  },

  // ─── Header ──────────────────────────────────────────────────────────────

  _renderHeader() {
    const header = this._el.querySelector('#tmp-header');
    const gold   = WorldState.partyGold ?? 0;
    const typeLabel = (this._townData.type ?? 'settlement')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());

    header.innerHTML = `
      <div class="tmp-header-left">
        <div class="tmp-town-name">${this._escHtml(this._townData.name)}</div>
        <div class="tmp-town-type">${this._escHtml(typeLabel)}</div>
      </div>
      <div class="tmp-header-right">
        <span class="tmp-gold-display">
          <span class="tmp-gold-icon">&#9670;</span>
          <span id="tmp-gold-value">${gold}g</span>
        </span>
        <button class="tmp-close-btn" aria-label="Leave town" title="Leave town (Esc)">&#10005;</button>
      </div>
    `;

    header.querySelector('.tmp-close-btn').addEventListener('click', () => this.close());
  },

  /** Refresh only the gold display (called after purchases). */
  _refreshGold() {
    const el = this._el?.querySelector('#tmp-gold-value');
    if (el) el.textContent = `${WorldState.partyGold ?? 0}g`;
  },

  // ─── Sidebar ─────────────────────────────────────────────────────────────

  _renderSidebar() {
    const sidebar = this._el.querySelector('#tmp-sidebar');
    sidebar.innerHTML = '';

    const servicesLabel = document.createElement('div');
    servicesLabel.className = 'tmp-sidebar-label';
    servicesLabel.textContent = 'Services';
    sidebar.appendChild(servicesLabel);

    for (const serviceId of SERVICE_ORDER) {
      if (!this._townData.services.includes(serviceId)) continue;
      const meta = SERVICE_META[serviceId];
      if (!meta) continue;

      const btn = document.createElement('button');
      btn.className = 'tmp-service-btn';
      btn.dataset.service = serviceId;
      btn.setAttribute('aria-pressed', 'false');
      btn.innerHTML = `<span class="tmp-service-icon">${meta.icon}</span>${this._escHtml(meta.label)}`;
      btn.addEventListener('click', () => this._selectService(serviceId));
      sidebar.appendChild(btn);
    }

    // Leave Town at bottom
    const leaveContainer = document.createElement('div');
    leaveContainer.className = 'tmp-leave-btn';

    const leaveBtn = document.createElement('button');
    leaveBtn.className = 'tmp-service-btn';
    leaveBtn.innerHTML = '<span class="tmp-service-icon">&#8594;</span>Leave Town';
    leaveBtn.addEventListener('click', () => this.close());
    leaveContainer.appendChild(leaveBtn);
    sidebar.appendChild(leaveContainer);
  },

  // ─── Service selection ────────────────────────────────────────────────────

  _selectService(serviceId) {
    this._activeService = serviceId;

    // Update sidebar active state
    this._el.querySelectorAll('.tmp-service-btn[data-service]').forEach(btn => {
      const active = btn.dataset.service === serviceId;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    // Render content
    const content = this._el.querySelector('#tmp-content');
    content.innerHTML = '';

    switch (serviceId) {
      case 'blacksmith':    this._renderBlacksmith(content);    break;
      case 'general_store': this._renderGeneralStore(content);  break;
      case 'inn':           this._renderInn(content);           break;
      case 'tavern':        this._renderTavern(content);        break;
      case 'temple':        this._renderTemple(content);        break;
      case 'party':         this._renderPartyManagement(content); break;
      case 'notices_board': this._renderNoticesBoard(content);  break;
      default:
        content.innerHTML = `<p style="color:#706050;padding:20px">This service is not available here.</p>`;
    }
  },

  // ─── Section renderers ────────────────────────────────────────────────────

  _renderBlacksmith(container) {
    const items = [
      { id: 'iron_sword',    name: 'Iron Sword',      desc: 'A dependable one-handed sword. Damage +4.',  price: 80  },
      { id: 'battle_axe',   name: 'Battle Axe',       desc: 'Heavy two-handed axe. Damage +6.',          price: 120 },
      { id: 'short_bow',    name: 'Short Bow',         desc: 'Compact ranged weapon. Range 4.',           price: 75  },
      { id: 'iron_shield',  name: 'Iron Shield',       desc: 'Grants +2 armor. One hand.',                price: 60  },
      { id: 'leather_vest', name: 'Leather Vest',      desc: 'Light armor. +1 armor, no speed penalty.',  price: 55  },
      { id: 'chain_shirt',  name: 'Chain Shirt',       desc: 'Medium armor. +3 armor.',                   price: 140 },
      { id: 'iron_helm',    name: 'Iron Helm',         desc: '+1 armor. Protects the head.',              price: 45  },
      { id: 'repair_kit',   name: 'Repair Kit',        desc: 'Restore 10 durability to one item.',        price: 30  },
    ];

    this._renderShopItems(container, 'Blacksmith', SERVICE_META.blacksmith.icon, items,
      'Heavy weapons and protective gear, forged on-site.');

    // ── Crafting section ───────────────────────────────────────────────────────
    this._renderCraftingSection(container);

    // Talk to blacksmith
    const talkSection = document.createElement('div');
    talkSection.style.cssText = 'margin-top:14px;';
    talkSection.innerHTML = `
      <div style="font-size:0.78rem;text-transform:uppercase;letter-spacing:0.1em;color:#605040;margin-bottom:8px;">Talk to the Blacksmith</div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;">
        <button class="tmp-action-btn" id="tmp-bs-talk-greet">Greet</button>
        <button class="tmp-action-btn" id="tmp-bs-talk-lore">Ask About Craft</button>
        <button class="tmp-action-btn" id="tmp-bs-talk-haggle">Try to Haggle</button>
      </div>
    `;
    container.appendChild(talkSection);

    container.querySelector('#tmp-bs-talk-greet').addEventListener('click', () => {
      DebugLog.info('[dialogue] Blacksmith: talk greet');
      DialoguePanel.speak('blacksmith', 'greeting');
    });
    container.querySelector('#tmp-bs-talk-lore').addEventListener('click', () => {
      DebugLog.info('[dialogue] Blacksmith: talk lore');
      DialoguePanel.speak('blacksmith', 'lore');
    });
    container.querySelector('#tmp-bs-talk-haggle').addEventListener('click', () => {
      DebugLog.info('[dialogue] Blacksmith: talk haggle');
      DialoguePanel.speak('blacksmith', 'haggle');
    });
  },

  /**
   * Render the crafting sub-section inside the blacksmith panel.
   * Shows all recipes with ingredient have/need counts; greys out unavailable ones.
   * @param {HTMLElement} container
   */
  _renderCraftingSection(container) {
    if (!CraftingSystem._initialized) CraftingSystem.init();

    // Resolve first party character for ingredient checks
    const party   = WorldState.party ?? [];
    const crafter = party[0] ?? null;

    const recipesWithStatus = CraftingSystem.getRecipesWithStatus(crafter);

    const section = document.createElement('div');
    section.id    = 'tmp-crafting-section';
    section.style.cssText = 'margin-top:20px;';

    section.innerHTML = `
      <div style="font-size:0.78rem;text-transform:uppercase;letter-spacing:0.1em;color:#605040;margin-bottom:10px;">Craft</div>
      <div id="tmp-crafting-list" style="display:flex;flex-direction:column;gap:6px;"></div>
      <div id="tmp-crafting-toast" style="
        display:none;
        margin-top:8px;
        padding:7px 12px;
        border-radius:4px;
        font-size:0.82rem;
        font-weight:600;
      "></div>
    `;
    container.appendChild(section);

    const list = section.querySelector('#tmp-crafting-list');

    for (const entry of recipesWithStatus) {
      const { recipe, canCraft, ingredients } = entry;
      const row = document.createElement('div');
      row.className = 'tmp-crafting-row';
      row.dataset.recipeId = recipe.id;
      row.style.cssText = `
        display:flex;
        align-items:flex-start;
        justify-content:space-between;
        gap:10px;
        padding:8px 10px;
        border-radius:4px;
        background:rgba(255,255,255,0.03);
        border:1px solid rgba(200,160,32,${canCraft ? '0.2' : '0.08'});
        opacity:${canCraft ? '1' : '0.55'};
      `;

      const ingHtml = ingredients
        .map(ing => {
          const ok = ing.have >= ing.count;
          const color = ok ? '#80c060' : '#c06050';
          const label = ing.item_id.replace(/_/g, ' ');
          return `<span style="color:${color};margin-right:8px;">${label}: ${ing.have}/${ing.count}</span>`;
        })
        .join('');

      const skillHtml = recipe.requiredSkill
        ? `<span style="color:#a080c0;margin-left:4px;">[${recipe.requiredSkill}]</span>`
        : '';

      row.innerHTML = `
        <div style="flex:1;min-width:0;">
          <div style="font-size:0.88rem;color:#e8d5b0;font-weight:600;margin-bottom:2px;">
            ${this._escHtml(recipe.name)}${skillHtml}
          </div>
          <div style="font-size:0.73rem;color:#907860;margin-bottom:4px;">${this._escHtml(recipe.description)}</div>
          <div style="font-size:0.72rem;display:flex;flex-wrap:wrap;">${ingHtml}</div>
        </div>
        <div style="display:flex;align-items:center;gap:8px;flex-shrink:0;">
          <span style="color:#d4aa40;font-size:0.82rem;">${recipe.goldCost}g</span>
          <button
            class="tmp-action-btn tmp-craft-btn"
            data-recipe-id="${recipe.id}"
            data-recipe-name="${this._escHtml(recipe.name)}"
            ${canCraft ? '' : 'disabled'}
            style="${canCraft ? '' : 'cursor:not-allowed;opacity:0.5;'}"
          >Craft</button>
        </div>
      `;

      list.appendChild(row);
    }

    // Event delegation for craft buttons
    list.addEventListener('click', (e) => {
      const btn = e.target.closest('.tmp-craft-btn');
      if (!btn || btn.disabled) return;

      const recipeId   = btn.dataset.recipeId;
      const recipeName = btn.dataset.recipeName;

      if (!crafter) {
        this._showCraftingToast(section, 'No party member available to craft.', 'error');
        return;
      }

      const result = CraftingSystem.craft(crafter, recipeId);

      if (result.success) {
        DebugLog.event(`[crafting] Panel craft success; recipe=${recipeId}`);
        this._showCraftingToast(section, `Crafted: ${recipeName}!`, 'success');
        this._refreshGold();
        // Re-render the crafting section with updated ingredient counts
        this._refreshCraftingSection(section, list);
      } else {
        DebugLog.warn(`[crafting] Panel craft failed; recipe=${recipeId} msg="${result.message}"`);
        this._showCraftingToast(section, result.message, 'error');
      }
    });
  },

  /**
   * Refresh crafting row states after a craft (update have/need counts and button states).
   * @param {HTMLElement} section — The #tmp-crafting-section element
   * @param {HTMLElement} list    — The #tmp-crafting-list element
   */
  _refreshCraftingSection(section, list) {
    const party   = WorldState.party ?? [];
    const crafter = party[0] ?? null;
    const recipesWithStatus = CraftingSystem.getRecipesWithStatus(crafter);

    for (const entry of recipesWithStatus) {
      const { recipe, canCraft, ingredients } = entry;
      const row = list.querySelector(`[data-recipe-id="${recipe.id}"]`);
      if (!row) continue;

      row.style.opacity = canCraft ? '1' : '0.55';
      row.style.borderColor = `rgba(200,160,32,${canCraft ? '0.2' : '0.08'})`;

      // Update ingredient counts
      const ingContainer = row.querySelector('div[style*="flex-wrap"]');
      if (ingContainer) {
        ingContainer.innerHTML = ingredients
          .map(ing => {
            const ok    = ing.have >= ing.count;
            const color = ok ? '#80c060' : '#c06050';
            const label = ing.item_id.replace(/_/g, ' ');
            return `<span style="color:${color};margin-right:8px;">${label}: ${ing.have}/${ing.count}</span>`;
          })
          .join('');
      }

      // Update craft button
      const btn = row.querySelector('.tmp-craft-btn');
      if (btn) {
        btn.disabled = !canCraft;
        btn.style.cursor    = canCraft ? '' : 'not-allowed';
        btn.style.opacity   = canCraft ? '' : '0.5';
      }
    }
  },

  /**
   * Show a brief success or error toast inside the crafting section.
   * Auto-hides after 2.5 seconds.
   * @param {HTMLElement} section
   * @param {string} message
   * @param {'success'|'error'} type
   */
  _showCraftingToast(section, message, type) {
    const toast = section.querySelector('#tmp-crafting-toast');
    if (!toast) return;

    const isSuccess = type === 'success';
    toast.textContent     = message;
    toast.style.display   = 'block';
    toast.style.background = isSuccess ? 'rgba(40,120,40,0.85)' : 'rgba(120,40,40,0.85)';
    toast.style.color      = isSuccess ? '#a0e880' : '#e88080';
    toast.style.border     = `1px solid ${isSuccess ? 'rgba(80,200,80,0.3)' : 'rgba(200,80,80,0.3)'}`;

    clearTimeout(this._craftToastTimer);
    this._craftToastTimer = setTimeout(() => {
      toast.style.display = 'none';
    }, 2500);
  },

  _renderGeneralStore(container) {
    const items = [
      { id: 'health_potion_small',  name: 'Small Health Potion', desc: 'Restores 15 HP.',           price: 20  },
      { id: 'health_potion_medium', name: 'Health Potion',        desc: 'Restores 35 HP.',           price: 40  },
      { id: 'antidote',             name: 'Antidote',             desc: 'Cures Poisoned status.',    price: 25  },
      { id: 'torch',                name: 'Torch (x5)',           desc: 'Provides light in caves.',  price: 10  },
      { id: 'rations_standard',     name: 'Travel Rations (x5)', desc: '+10 ration points.',        price: 15  },
      { id: 'rope',                 name: 'Rope (15m)',           desc: 'Useful in many situations.',price: 12  },
      { id: 'bandage',              name: 'Bandage',              desc: 'Stops Bleeding status.',    price: 8   },
      { id: 'camping_kit',          name: 'Camping Kit',          desc: '+2 rest bonus to next camp.',price: 50 },
    ];

    this._renderShopItems(container, 'General Store', SERVICE_META.general_store.icon, items,
      'Supplies, consumables, and everyday goods for the road.');
  },

  _renderShopItems(container, title, icon, items, desc) {
    container.innerHTML = `
      <div class="tmp-section-title">${icon} ${this._escHtml(title)}</div>
      <div class="tmp-section-desc">${this._escHtml(desc)}</div>
      <div class="tmp-item-list" id="tmp-shop-list"></div>
    `;

    const list = container.querySelector('#tmp-shop-list');
    for (const item of items) {
      const row = document.createElement('div');
      row.className = 'tmp-item-row';
      row.innerHTML = `
        <div class="tmp-item-info">
          <div class="tmp-item-name">${this._escHtml(item.name)}</div>
          <div class="tmp-item-desc">${this._escHtml(item.desc)}</div>
        </div>
        <div class="tmp-item-actions">
          <span class="tmp-item-price">${item.price}g</span>
          <button class="tmp-action-btn" data-action="buy" data-item="${item.id}" data-price="${item.price}">Buy</button>
        </div>
      `;
      list.appendChild(row);
    }

    list.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action="buy"]');
      if (!btn) return;
      const price  = parseInt(btn.dataset.price, 10);
      const itemId = btn.dataset.item;
      this._handleBuy(itemId, price, btn);
    });
  },

  _handleBuy(itemId, price, btn) {
    const gold = WorldState.partyGold ?? 0;
    if (gold < price) {
      this._toast('Not enough gold.', 'error');
      return;
    }

    WorldState.partyGold = gold - price;
    this._refreshGold();

    // Give item if inventory system is ready
    if (window.__inventoryManager?.addItem) {
      window.__inventoryManager.addItem(null, itemId, 1);
    }

    DebugLog.event(`[item] Purchased ${itemId} for ${price}g`);
    this._toast(`Purchased: ${itemId.replace(/_/g, ' ')}`, 'success');
  },

  _renderInn(container) {
    const innName   = INN_NAMES[Math.floor(Math.random() * INN_NAMES.length)];
    const partySize = (WorldState.party?.length ?? 1);
    const costPerPerson = 8;
    const totalCost = partySize * costPerPerson;

    container.innerHTML = `
      <div class="tmp-section-title">${SERVICE_META.inn.icon} ${this._escHtml(innName)}</div>
      <div class="tmp-section-desc">Rest the night, restore HP and remove minor status effects. Shared room rate.</div>
      <div class="tmp-confirm-box">
        <div class="tmp-confirm-title">Rest for the Night</div>
        <div class="tmp-confirm-body">
          Party size: ${partySize} person${partySize !== 1 ? 's' : ''}<br>
          Cost: ${costPerPerson}g per person &mdash; <strong style="color:#d4aa40">${totalCost}g total</strong><br><br>
          Everyone will recover to full HP and Stamina. Status effects <em>Exhausted</em>, <em>Chilled</em>,
          and <em>Minor Wounds</em> will be cleared.
        </div>
        <div class="tmp-confirm-actions">
          <button class="tmp-action-btn" id="tmp-inn-rest">Rest (${totalCost}g)</button>
          <button class="tmp-action-btn" id="tmp-inn-cancel" style="background:rgba(80,40,10,0.2);border-color:rgba(160,100,30,0.3);color:#907860">Cancel</button>
        </div>
      </div>
    `;

    container.querySelector('#tmp-inn-rest').addEventListener('click', () => {
      const gold = WorldState.partyGold ?? 0;
      if (gold < totalCost) { this._toast('Not enough gold.', 'error'); return; }
      WorldState.partyGold = gold - totalCost;
      this._refreshGold();
      EventBus.emit('party:rest', { type: 'inn', quality: 'full' });
      DebugLog.event(`[party] Party rested at inn "${innName}"; cost=${totalCost}g`);
      this._toast('The party rests well. All HP restored.', 'success');
    });

    container.querySelector('#tmp-inn-cancel').addEventListener('click', () => {
      this._toast('Perhaps another night.', 'info');
    });

    // Talk to innkeeper
    const talkSection = document.createElement('div');
    talkSection.style.cssText = 'margin-top:14px;';
    talkSection.innerHTML = `
      <div style="font-size:0.78rem;text-transform:uppercase;letter-spacing:0.1em;color:#605040;margin-bottom:8px;">Talk to the Innkeeper</div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;">
        <button class="tmp-action-btn" id="tmp-inn-talk-greet">Greet</button>
        <button class="tmp-action-btn" id="tmp-inn-talk-rumor">Ask for Rumors</button>
        <button class="tmp-action-btn" id="tmp-inn-talk-hint">Ask for Work</button>
      </div>
    `;
    container.appendChild(talkSection);

    container.querySelector('#tmp-inn-talk-greet').addEventListener('click', () => {
      DebugLog.info('[dialogue] Inn: talk greet');
      DialoguePanel.speak('innkeeper', 'greeting');
    });
    container.querySelector('#tmp-inn-talk-rumor').addEventListener('click', () => {
      DebugLog.info('[dialogue] Inn: talk rumor');
      DialoguePanel.speak('innkeeper', 'rumor');
    });
    container.querySelector('#tmp-inn-talk-hint').addEventListener('click', () => {
      DebugLog.info('[dialogue] Inn: talk quest_hint');
      DialoguePanel.speak('innkeeper', 'quest_hint');
    });
  },

  _renderTavern(container) {
    container.innerHTML = `
      <div class="tmp-section-title">${SERVICE_META.tavern.icon} The Tavern</div>
      <div class="tmp-section-desc">Hire mercenaries, hear local rumors, and find work.</div>
    `;

    // Rumors section
    const rumorTitle = document.createElement('div');
    rumorTitle.style.cssText = 'font-size:0.78rem;text-transform:uppercase;letter-spacing:0.1em;color:#605040;margin-top:8px;';
    rumorTitle.textContent = 'Rumors Heard Tonight';
    container.appendChild(rumorTitle);

    const rumorCount = 2 + Math.floor(Math.random() * 2);
    const shuffled   = [...TAVERN_RUMORS].sort(() => Math.random() - 0.5).slice(0, rumorCount);
    for (const rumor of shuffled) {
      const box = document.createElement('div');
      box.className = 'tmp-rumor-box';
      box.innerHTML = `"${this._escHtml(rumor)}" <span class="tmp-rumor-source">— overheard at the bar</span>`;
      container.appendChild(box);
    }

    // Mercenaries section
    const mercTitle = document.createElement('div');
    mercTitle.style.cssText = 'font-size:0.78rem;text-transform:uppercase;letter-spacing:0.1em;color:#605040;margin-top:18px;';
    mercTitle.textContent = 'Mercenaries for Hire';
    container.appendChild(mercTitle);

    // Load hireable NPCs
    const npcs = this._getHireableNPCs();
    const available = npcs.slice(0, 4); // Show up to 4 in tavern

    for (const npc of available) {
      const row = document.createElement('div');
      row.className = 'tmp-merc-row';
      const wagePerDay = npc.wagePerDay ?? 5;
      row.innerHTML = `
        <div>
          <div class="tmp-merc-name">${this._escHtml(npc.name)}</div>
          <div class="tmp-merc-class">${this._escHtml(npc.class ?? 'Fighter')} &mdash; Lv.${npc.baseLevel ?? 1}</div>
          <div style="font-size:0.7rem;color:#706050;margin-top:2px;">${this._escHtml(npc.description ?? '')}</div>
        </div>
        <span class="tmp-merc-wage">${wagePerDay}g/day</span>
        <button class="tmp-action-btn" data-hire="${npc.id}" data-wage="${wagePerDay}">Hire</button>
      `;
      container.appendChild(row);
    }

    if (!available.length) {
      const empty = document.createElement('div');
      empty.style.cssText = 'color:#605040;font-size:0.82rem;padding:12px;';
      empty.textContent = 'No mercenaries are available at this time.';
      container.appendChild(empty);
    }

    container.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-hire]');
      if (!btn) return;
      const npcId    = btn.dataset.hire;
      const wagePerDay = parseInt(btn.dataset.wage, 10);
      EventBus.emit('party:hire_npc', { npcId, wagePerDay });
      DebugLog.event(`[party] Hired mercenary; npcId=${npcId} wage=${wagePerDay}g/day`);
      this._toast(`Mercenary hired. Wage: ${wagePerDay}g/day.`, 'success');
      btn.disabled = true;
    });

    // Talk to barkeep
    const talkSection = document.createElement('div');
    talkSection.style.cssText = 'margin-top:18px;';
    talkSection.innerHTML = `
      <div style="font-size:0.78rem;text-transform:uppercase;letter-spacing:0.1em;color:#605040;margin-bottom:8px;">Talk to the Barkeep</div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;">
        <button class="tmp-action-btn" id="tmp-tavern-talk-greet">Greet</button>
        <button class="tmp-action-btn" id="tmp-tavern-talk-rumor">Ask for Rumors</button>
        <button class="tmp-action-btn" id="tmp-tavern-talk-song">Request a Song</button>
      </div>
    `;
    container.appendChild(talkSection);

    container.querySelector('#tmp-tavern-talk-greet').addEventListener('click', () => {
      DebugLog.info('[dialogue] Tavern: talk greet');
      DialoguePanel.speak('tavern_barkeep', 'greeting');
    });
    container.querySelector('#tmp-tavern-talk-rumor').addEventListener('click', () => {
      DebugLog.info('[dialogue] Tavern: talk rumor');
      DialoguePanel.speak('tavern_barkeep', 'rumor');
    });
    container.querySelector('#tmp-tavern-talk-song').addEventListener('click', () => {
      DebugLog.info('[dialogue] Tavern: talk drinking_song');
      DialoguePanel.speak('tavern_barkeep', 'drinking_song');
    });
  },

  _renderTemple(container) {
    const partySize = WorldState.party?.length ?? 1;

    container.innerHTML = `
      <div class="tmp-section-title">${SERVICE_META.temple.icon} Temple</div>
      <div class="tmp-section-desc">Healing services, status cure, and blessings from the priests.</div>
      <div class="tmp-item-list"></div>
    `;

    const services = [
      {
        id: 'full_heal',
        name: 'Full Party Healing',
        desc: 'Restore all party members to full HP.',
        price: 20 * partySize,
        action: () => {
          EventBus.emit('party:heal', { type: 'full', target: 'all' });
          DebugLog.event('[party] Temple full heal performed');
          this._toast('The priests lay hands on your party. All wounds are healed.', 'success');
        },
      },
      {
        id: 'cure_status',
        name: 'Cure All Status Effects',
        desc: 'Remove Poisoned, Cursed, Bleeding, and all debuffs from the party.',
        price: 15 * partySize,
        action: () => {
          EventBus.emit('party:cure', { target: 'all' });
          DebugLog.event('[party] Temple status cure performed');
          this._toast('Ailments cleansed. The party feels lighter.', 'success');
        },
      },
      {
        id: 'bless',
        name: 'Battle Blessing',
        desc: '+1 to all attack and skill rolls for the next 3 combat encounters.',
        price: 30,
        action: () => {
          EventBus.emit('party:bless', { duration: 3, bonus: 1 });
          DebugLog.event('[party] Temple blessing applied');
          this._toast('The gods grant their favor. The party is blessed.', 'success');
        },
      },
      {
        id: 'resurrect',
        name: 'Resurrect Fallen',
        desc: 'Bring a fallen party member back with 1 HP. Requires a body.',
        price: 200,
        action: () => {
          EventBus.emit('party:resurrect', {});
          DebugLog.event('[party] Temple resurrection performed');
          this._toast('The fallen breathe again. Resurrection complete.', 'success');
        },
      },
    ];

    const list = container.querySelector('.tmp-item-list');
    for (const svc of services) {
      const row = document.createElement('div');
      row.className = 'tmp-item-row';
      row.innerHTML = `
        <div class="tmp-item-info">
          <div class="tmp-item-name">${this._escHtml(svc.name)}</div>
          <div class="tmp-item-desc">${this._escHtml(svc.desc)}</div>
        </div>
        <div class="tmp-item-actions">
          <span class="tmp-item-price">${svc.price}g</span>
          <button class="tmp-action-btn" data-svc="${svc.id}" data-price="${svc.price}">Purchase</button>
        </div>
      `;
      list.appendChild(row);
    }

    list.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-svc]');
      if (!btn) return;
      const price = parseInt(btn.dataset.price, 10);
      const svcId = btn.dataset.svc;
      const gold  = WorldState.partyGold ?? 0;
      if (gold < price) { this._toast('Not enough gold.', 'error'); return; }
      WorldState.partyGold = gold - price;
      this._refreshGold();
      services.find(s => s.id === svcId)?.action?.();
    });
  },

  _renderNoticesBoard(container) {
    const level = this._townData?.level ?? 1;

    container.innerHTML = `
      <div class="tmp-section-title">${SERVICE_META.notices_board.icon} Notices Board</div>
      <div class="tmp-section-desc">Job postings, bounties, and requests from the community. New notices appear daily.</div>
      <div class="tmp-item-list" id="tmp-notices-list"></div>
    `;

    const notices = this._generateNotices(level);
    const list = container.querySelector('#tmp-notices-list');

    for (const notice of notices) {
      const row = document.createElement('div');
      row.className = 'tmp-item-row';
      row.innerHTML = `
        <div class="tmp-item-info">
          <div class="tmp-item-name">${this._escHtml(notice.title)}</div>
          <div class="tmp-item-desc">${this._escHtml(notice.desc)}</div>
        </div>
        <div class="tmp-item-actions">
          <span class="tmp-item-price" style="color:#9ab870;">${this._escHtml(notice.reward)}</span>
          <button class="tmp-action-btn" data-notice-id="${this._escHtml(notice.id)}">Accept</button>
        </div>
      `;
      list.appendChild(row);
    }

    list.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-notice-id]');
      if (!btn || btn.disabled) return;
      const noticeId = btn.dataset.noticeId;
      const notice   = notices.find(n => n.id === noticeId);

      DebugLog.event(`[quest] Notice accepted; id=${noticeId} title="${notice?.title}"`);

      // Try to trigger an encounter if available; otherwise just log acceptance
      if (typeof window.__debug?.triggerEncounter === 'function') {
        window.__debug.triggerEncounter();
      }

      EventBus.emit('quest:notice_accepted', { noticeId, notice });
      this._toast(`Accepted: ${notice?.title ?? noticeId}`, 'success');
      btn.disabled = true;
      btn.textContent = 'Accepted';
    });
  },

  /**
   * Procedurally generate 3 job notices scaled to town level.
   * @param {number} level
   * @returns {Array<{id:string, title:string, desc:string, reward:string}>}
   */
  _generateNotices(level) {
    const gold = (base, perLevel) => `${base + (level - 1) * perLevel}g`;

    const NOTICE_TEMPLATES = [
      {
        id: 'bounty_goblins',
        title: 'Bounty: Goblin Scouts',
        desc: `Eliminate 3 goblin scouts reported near the eastern trail. Bring proof of the deed.`,
        reward: gold(80, 15),
      },
      {
        id: 'delivery_helgard',
        title: 'Delivery to Helgard',
        desc: `Carry a sealed crate to the settlement at Helgard. Handle with care — contents unknown.`,
        reward: `${gold(50, 10)} + rations`,
      },
      {
        id: 'clear_cave',
        title: 'Clear the Eastern Cave',
        desc: `Something has moved into the cave east of town. Farmers report missing livestock. Clear whatever is inside.`,
        reward: gold(120, 20),
      },
      {
        id: 'escort_merchant',
        title: 'Escort Needed: River Road',
        desc: `A merchant requires armed escort to the next waypoint on the River Road. Depart at dawn.`,
        reward: gold(60, 12),
      },
      {
        id: 'missing_hunter',
        title: 'Find the Missing Hunter',
        desc: `Oskar the trapper did not return from the Ashwood three days ago. His family offers a reward for news of him.`,
        reward: gold(40, 8),
      },
      {
        id: 'herb_collection',
        title: 'Herb Collection Needed',
        desc: `The town healer requires Frostmoss and Redroot from the marshes to the north. Fresh only.`,
        reward: gold(35, 7),
      },
      {
        id: 'patrol_ruins',
        title: 'Patrol the Old Ruins',
        desc: `Strange lights have been seen at the old ruins north of the mill. The town watch needs someone to investigate and report back.`,
        reward: gold(90, 18),
      },
    ];

    // Deterministically pick 3 based on level seed for consistency within a session
    const seed = level * 17 + (this._townData?.name?.charCodeAt(0) ?? 7);
    const shuffled = [...NOTICE_TEMPLATES].sort((a, b) => {
      const ha = (a.id.charCodeAt(0) * seed) % 100;
      const hb = (b.id.charCodeAt(0) * seed) % 100;
      return ha - hb;
    });

    return shuffled.slice(0, 3);
  },

  _renderPartyManagement(container) {
    container.innerHTML = `
      <div class="tmp-section-title">${SERVICE_META.party.icon} Party Management</div>
      <div class="tmp-section-desc">Review your party's status, hire companions, and manage your roster.</div>
    `;

    const party = WorldState.party ?? [];
    const gold  = WorldState.partyGold ?? 0;

    // ── Current Party ───────────────────────────────────────────────────────

    const currentTitle = document.createElement('div');
    currentTitle.style.cssText = 'font-size:0.8rem;color:#d4aa40;text-transform:uppercase;letter-spacing:0.08em;margin:12px 0 6px;';
    currentTitle.textContent = 'Current Party';
    container.appendChild(currentTitle);

    if (!party.length) {
      const empty = document.createElement('p');
      empty.style.cssText = 'color:#706050;padding:8px 0;font-size:0.85rem;';
      empty.textContent = 'No party data available yet. Start a new game to create characters.';
      container.appendChild(empty);
    } else {
      for (const member of party) {
        const card = document.createElement('div');
        card.className = 'tmp-item-row';
        card.style.flexDirection = 'column';
        card.style.alignItems    = 'flex-start';
        card.style.gap           = '8px';

        const maxHp = member.maxHP ?? member.maxHp ?? 1;
        const hpPct = maxHp > 0
          ? Math.round(((member.hp ?? 0) / maxHp) * 100)
          : 100;

        const wageStr = member.isCompanion
          ? `<span style="font-size:0.75rem;color:#b8903a;">Wage: ${member.weeklyWage ?? 0}g/week</span>`
          : '<span style="font-size:0.75rem;color:#506040;">Hero</span>';

        card.innerHTML = `
          <div style="display:flex;justify-content:space-between;width:100%;align-items:center;">
            <div>
              <div class="tmp-item-name">${this._escHtml(member.name ?? 'Unknown')}</div>
              <div class="tmp-item-desc">${this._escHtml(member.class ?? '')} &mdash; Level ${member.level ?? 1} &mdash; ${this._escHtml(member.race ?? '')}</div>
              ${wageStr}
            </div>
            <div style="text-align:right;">
              <div style="font-size:0.78rem;color:#d4aa40;">HP: ${member.hp ?? '?'}/${maxHp}</div>
              <div style="font-size:0.7rem;color:#606050;margin-top:2px;">
                <span style="display:inline-block;width:80px;height:6px;background:rgba(80,60,30,0.5);border-radius:3px;overflow:hidden;vertical-align:middle;">
                  <span style="display:block;height:100%;width:${hpPct}%;background:${hpPct > 50 ? '#4a8a30' : hpPct > 25 ? '#9a8020' : '#8a3020'};border-radius:3px;"></span>
                </span>
                ${hpPct}%
              </div>
            </div>
          </div>
        `;

        // Dismiss button for companions
        if (member.isCompanion && member.companionId) {
          const dismissBtn = document.createElement('button');
          dismissBtn.className = 'tmp-action-btn';
          dismissBtn.style.cssText = 'background:rgba(120,40,20,0.6);font-size:0.78rem;padding:4px 12px;margin-top:2px;';
          dismissBtn.textContent = 'Dismiss';
          dismissBtn.addEventListener('click', () => {
            const result = CompanionSystem.dismiss(member.companionId);
            this._toast(result.message, result.success ? 'info' : 'error');
            if (result.success) {
              this._renderPartyManagement(container);
            }
          });
          card.appendChild(dismissBtn);
        }

        container.appendChild(card);
      }
    }

    // ── Available for Hire ──────────────────────────────────────────────────

    const hireTitle = document.createElement('div');
    hireTitle.style.cssText = 'font-size:0.8rem;color:#d4aa40;text-transform:uppercase;letter-spacing:0.08em;margin:18px 0 6px;border-top:1px solid rgba(200,160,32,0.15);padding-top:14px;';
    hireTitle.textContent = 'Available for Hire';
    container.appendChild(hireTitle);

    const goldLabel = document.createElement('div');
    goldLabel.style.cssText = 'font-size:0.8rem;color:#b8903a;margin-bottom:10px;';
    goldLabel.textContent = `Party Gold: ${gold}g`;
    container.appendChild(goldLabel);

    // Ensure CompanionSystem is initialised
    if (!CompanionSystem._companions?.length) {
      CompanionSystem.init();
    }

    const available = CompanionSystem.getAvailableForHire({
      name:  this._townData?.name  ?? 'Town',
      level: this._townData?.level ?? 1,
    });

    if (!available.length) {
      const noComp = document.createElement('p');
      noComp.style.cssText = 'color:#706050;font-size:0.85rem;padding:8px 0;';
      noComp.textContent = 'No companions available in this town right now.';
      container.appendChild(noComp);
    } else {
      for (const def of available) {
        const card = document.createElement('div');
        card.className = 'tmp-item-row tmp-companion-card';
        card.dataset.companionId = def.id;
        card.style.flexDirection = 'column';
        card.style.alignItems    = 'flex-start';
        card.style.gap           = '6px';
        card.style.position      = 'relative';

        const canAfford   = gold >= (def.hireCost ?? 0);
        const partyFull   = party.length >= 4;
        const alreadyHired = CompanionSystem.isHired(def.id);

        const disableReason = alreadyHired ? 'Already hired'
          : partyFull ? 'Party full'
          : !canAfford ? `Need ${def.hireCost}g`
          : null;

        const raceName = def.race
          ? def.race.charAt(0).toUpperCase() + def.race.slice(1)
          : 'Unknown';

        card.innerHTML = `
          <div style="display:flex;justify-content:space-between;width:100%;align-items:flex-start;">
            <div style="flex:1;min-width:0;">
              <div class="tmp-item-name">${this._escHtml(def.name)}</div>
              <div class="tmp-item-desc" style="margin-bottom:4px;">
                ${this._escHtml(raceName)} &mdash; Level ${def.startingLevel ?? 1}
                &mdash; ${this._escHtml((def.personality ?? 'unknown').charAt(0).toUpperCase() + (def.personality ?? '').slice(1))}
              </div>
              <div style="font-size:0.8rem;color:#a09070;line-height:1.4;">${this._escHtml(def.description ?? '')}</div>
              ${def.dialogue?.[0] ? `<div style="font-size:0.78rem;color:#706050;font-style:italic;margin-top:4px;">"${this._escHtml(def.dialogue[0])}"</div>` : ''}
            </div>
            <div style="text-align:right;flex-shrink:0;margin-left:12px;">
              <div style="font-size:0.9rem;color:#d4aa40;font-weight:bold;">${def.hireCost ?? 0}g</div>
              <div style="font-size:0.72rem;color:#806040;margin-top:2px;">${def.weeklyWage ?? 0}g/week</div>
            </div>
          </div>
        `;

        const hireBtn = document.createElement('button');
        hireBtn.className = 'tmp-action-btn tmp-companion-hire-btn';
        hireBtn.dataset.companionId = def.id;
        hireBtn.style.cssText = 'margin-top:4px;font-size:0.82rem;padding:5px 16px;';
        hireBtn.textContent = disableReason ? disableReason : `Hire — ${def.hireCost}g`;

        if (disableReason) {
          hireBtn.disabled = true;
          hireBtn.style.opacity = '0.5';
          hireBtn.style.cursor  = 'default';
        } else {
          hireBtn.addEventListener('click', () => {
            const result = CompanionSystem.hire(def.id, WorldState.party ?? []);
            this._toast(result.message, result.success ? 'success' : 'error');
            if (result.success) {
              // Refresh panel to reflect new hire
              this._renderPartyManagement(container);
            }
          });
        }

        card.appendChild(hireBtn);
        container.appendChild(card);
      }
    }

    // ── Full Party Manager button ───────────────────────────────────────────

    const manageBtn = document.createElement('button');
    manageBtn.className = 'tmp-action-btn';
    manageBtn.style.marginTop = '14px';
    manageBtn.textContent = 'Open Full Party Manager';
    manageBtn.addEventListener('click', () => {
      window.__managePlayersPanel?.toggle();
    });
    container.appendChild(manageBtn);
  },

  // ─── Helpers ─────────────────────────────────────────────────────────────

  _renderStatCells(attrs) {
    const stats = ['might', 'agility', 'intellect', 'wisdom', 'endurance', 'presence'];
    return stats.map(s => `
      <div class="tmp-stat-cell">
        <span class="tmp-stat-label">${s.slice(0, 3).toUpperCase()}</span>
        <span class="tmp-stat-value">${attrs[s] ?? '--'}</span>
      </div>
    `).join('');
  },

  _getHireableNPCs() {
    // Try to use loaded data, fall back to a minimal inline sample
    if (window.__hireableNPCs?.length) return window.__hireableNPCs;

    return [
      { id: 'hired_warrior', name: 'Sellsword',       class: 'warrior',  baseLevel: 1, wagePerDay: 5,  description: 'A battle-hardened sellsword who fights for coin.' },
      { id: 'hired_archer',  name: 'Bowman',           class: 'archer',   baseLevel: 1, wagePerDay: 5,  description: 'A skilled archer whose arrows find their mark.' },
      { id: 'hired_mage',    name: 'Wandering Mage',   class: 'mage',     baseLevel: 2, wagePerDay: 8,  description: 'A mage seeking adventure and arcane knowledge.' },
      { id: 'hired_healer',  name: 'Wandering Healer', class: 'healer',   baseLevel: 2, wagePerDay: 8,  description: 'A healer seeking safe passage to the northern lands.' },
    ];
  },

  /**
   * Show a brief toast notification inside the panel.
   * @param {string} msg
   * @param {'success'|'error'|'info'} [type='info']
   */
  _toast(msg, type = 'info') {
    const existing = this._el?.querySelector('.tmp-toast');
    existing?.remove();

    const t = document.createElement('div');
    t.className = 'tmp-toast';
    const color = type === 'success' ? '#4a8a30'
                : type === 'error'   ? '#8a3020'
                : '#3a5a80';
    t.style.cssText = `
      position:fixed;
      bottom:24px;
      left:50%;
      transform:translateX(-50%);
      background:${color};
      color:#f0f0e0;
      padding:10px 22px;
      border-radius:4px;
      font-family:Georgia,serif;
      font-size:0.88rem;
      z-index:260;
      pointer-events:none;
      box-shadow:0 3px 12px rgba(0,0,0,0.5);
      white-space:nowrap;
    `;
    t.textContent = msg;
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 2400);
  },

  /** HTML-escape a string for safe innerHTML insertion. */
  _escHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  },
};

export default TownMenuPanel;
